import { Alert, LinearProgress, TextField } from '@mui/material';
import { addDays, format } from 'date-fns';
import { useEffect, useState } from 'react';

import {
  buildDirectAnnouncementPayload,
  MAX_CONTENT_LENGTH,
  validateTestAnnouncement,
  type DirectAnnouncementPayload,
  type WorkerSearchRow,
} from './lib';

import Avatar from '@/components/Avatar';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import {
  testAnnouncementAPI,
  type SendDirectResult,
} from '@/services/testAnnouncementAPI';

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 14,
  outline: 'none',
};
const resultsBoxStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 6,
  marginTop: 4,
  maxHeight: 260,
  overflowY: 'auto',
};
const rowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  padding: '10px 12px',
  cursor: 'pointer',
  alignItems: 'center',
  borderBottom: '1px solid #f3f4f6',
};
const emptyStyle: React.CSSProperties = {
  padding: 16,
  color: '#6b7280',
  fontSize: 13,
};
const chipRowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  marginTop: 10,
};
const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: '#eff6ff',
  border: '1px solid #bfdbfe',
  borderRadius: 16,
  padding: '4px 8px 4px 10px',
  fontSize: 13,
};
const chipRemoveStyle: React.CSSProperties = {
  cursor: 'pointer',
  color: '#2563eb',
  fontWeight: 700,
};

function workerLabel(worker: WorkerSearchRow): string {
  const first = worker.user?.first_name ?? '';
  const last = worker.user?.last_name ?? '';
  const composed = `${first} ${last}`.trim();
  if (composed.length > 0) return composed;
  return worker.user?.email ?? 'Worker';
}

type TestAnnouncementVariant = 'pubnub' | 'supabase';

const VARIANTS: Record<
  TestAnnouncementVariant,
  {
    pageTitle: string;
    stackLabel: string;
    warning: string;
    idLabel: string;
    send: (payload: DirectAnnouncementPayload) => Promise<SendDirectResult>;
  }
> = {
  pubnub: {
    pageTitle: 'Test Announcement',
    stackLabel: 'Legacy — PubNub',
    warning:
      'This is a real send. The workers you pick will receive a live announcement and a ChatGroup record is created — it is only isolated to them, not suppressed.',
    idLabel: 'Announcement #',
    send: (payload) => testAnnouncementAPI.sendDirect(payload),
  },
  supabase: {
    pageTitle: 'Test Announcement V2',
    stackLabel: 'Chat V2 — Supabase',
    warning:
      'This is a real send. The workers you pick will receive a live Chat V2 broadcast (Supabase) — it is only isolated to them, not suppressed.',
    idLabel: 'Broadcast ',
    send: (payload) => testAnnouncementAPI.sendDirectSupabase(payload),
  },
};

const TestAnnouncement = ({
  variant = 'pubnub',
}: {
  variant?: TestAnnouncementVariant;
}) => {
  const cfg = VARIANTS[variant];
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [expDate, setExpDate] = useState(
    format(addDays(new Date(), 3), 'yyyy-MM-dd'),
  );
  const [expTime, setExpTime] = useState('23:59');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<WorkerSearchRow[]>([]);
  const [searching, setSearching] = useState(false);
  const [workers, setWorkers] = useState<WorkerSearchRow[]>([]);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await testAnnouncementAPI.searchWorkers(trimmed);
        if (!cancelled) setResults(rows.filter((r) => r.user != null));
      } catch (e) {
        if (!cancelled) setResults([]);
        console.error('[TestAnnouncement] worker search error:', e);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  const addWorker = (worker: WorkerSearchRow) => {
    setWorkers((prev) =>
      prev.some((w) => w.user?.id === worker.user?.id)
        ? prev
        : [...prev, worker],
    );
    setQuery('');
    setResults([]);
  };

  const removeWorker = (userId: number | undefined) => {
    setWorkers((prev) => prev.filter((w) => w.user?.id !== userId));
  };

  const handleSend = async () => {
    setSuccess('');
    const form = { title, message, expDate, expTime, workers };
    const errMsg = validateTestAnnouncement(form);
    if (errMsg) {
      setError(errMsg);
      return;
    }
    setError('');
    setSending(true);
    try {
      const payload = buildDirectAnnouncementPayload(form);
      const res = await cfg.send(payload);
      if (res.success) {
        setSuccess(
          `Sent to ${payload.userIds.length} worker(s). ${cfg.idLabel}${res.id ?? '—'} created.`,
        );
        setWorkers([]);
        setTitle('');
        setMessage('');
      } else {
        setError(res.errors?.[0] ?? 'Send failed.');
      }
    } catch (e: unknown) {
      const apiMsg =
        (e as { response?: { data?: { errors?: string[]; message?: string } } })
          ?.response?.data?.errors?.[0] ??
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ??
        (e as Error)?.message;
      setError(apiMsg ?? 'Failed to send announcement.');
      console.error('[TestAnnouncement] send error:', e);
    } finally {
      setSending(false);
    }
  };

  return (
    <Page size="full" title={cfg.pageTitle}>
      <Card
        title={`Send a direct announcement (isolated to specific workers) — ${cfg.stackLabel}`}
      >
        <Card.Section>
          <Stack vertical gap={16}>
            <Alert severity="warning">{cfg.warning}</Alert>

            <div>
              <Body>Workers</Body>
              <input
                type="search"
                placeholder="Search workers by name or email…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={searchInputStyle}
              />
              {query.trim().length >= 2 && (
                <div style={resultsBoxStyle}>
                  {searching ? (
                    <div style={emptyStyle}>Searching…</div>
                  ) : results.length === 0 ? (
                    <div style={emptyStyle}>No matching workers.</div>
                  ) : (
                    results.map((worker) => (
                      <div
                        key={worker.id}
                        onClick={() => addWorker(worker)}
                        style={rowStyle}
                      >
                        <Avatar
                          firstName={workerLabel(worker)}
                          src={worker.avatar_url ?? undefined}
                          size="sm"
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 500 }}>
                            {workerLabel(worker)}
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7280' }}>
                            {worker.user?.email ?? ''}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              {workers.length > 0 && (
                <div style={chipRowStyle}>
                  {workers.map((worker) => (
                    <span key={worker.user?.id} style={chipStyle}>
                      {workerLabel(worker)}
                      <span
                        onClick={() => removeWorker(worker.user?.id)}
                        style={chipRemoveStyle}
                        aria-label="Remove worker"
                      >
                        ✕
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <TextField
              fullWidth
              label="Title"
              inputProps={{ maxLength: 250 }}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />

            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
              label="Message"
              inputProps={{ maxLength: MAX_CONTENT_LENGTH }}
              helperText={`${message.length}/${MAX_CONTENT_LENGTH}`}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />

            <Stack gap={16}>
              <div>
                <Body>Expiry date</Body>
                <input
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  value={expDate}
                  onChange={(e) => {
                    if (e.target.value) setExpDate(e.target.value);
                  }}
                  style={searchInputStyle}
                />
              </div>
              <div>
                <Body>Expiry time</Body>
                <input
                  type="time"
                  value={expTime}
                  onChange={(e) => {
                    if (e.target.value) setExpTime(e.target.value);
                  }}
                  style={searchInputStyle}
                />
              </div>
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}
            {sending && <LinearProgress />}

            <Button
              a11yLabel="Send test announcement"
              label={sending ? 'Sending…' : 'Send test announcement'}
              disabled={sending || workers.length === 0}
              onClick={handleSend}
            />
          </Stack>
        </Card.Section>
      </Card>
    </Page>
  );
};

export default TestAnnouncement;
